import { useState, useCallback } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { ICustomerContact } from 'apps/fleet-planner/types'
import * as Types from 'apps/fleet-planner/types'
import * as Api from 'apps/fleet-planner/utils/api'

interface IUseContactApiState {
  error?: string
  isLoading: boolean
  contacts: ICustomerContact[]
  resetError: () => void
  getContacts: (searchQuery: string) => Promise<Types.ICustomerContact[] | undefined>
}

export const useContactApi = (): IUseContactApiState => {
  const [contacts, setContacts] = useState<ICustomerContact[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const resetError = useCallback(() => setError(''), [])

  /**
   * Fetch contacts list depending on search params
   */
  const _getContacts = useCallback(async (searchQuery: string): Promise<
    ICustomerContact[] | undefined
  > => {
    if (!searchQuery) {
      setError('')
      setContacts([])
      return undefined
    }
    setIsLoading(true)
    try {
      const { contacts: response = [] } = await Api.fetchSubscribableContacts(searchQuery)
      setContacts(response)
      setIsLoading(false)
      return response
    } catch (e) {
      setError(e)
      setIsLoading(false)
      return undefined
    }
  }, [])

  // Debounced callback function that's used in component
  const [getContacts] = useDebouncedCallback(_getContacts, 200, { leading: true })

  return {
    error,
    resetError,
    isLoading,
    getContacts,
    contacts,
  }
}
